// A pixel-art painter for portraits. Shapes (ellipses, bevelled polygons,
// smooth blobs and tapered strokes) are rasterised at native resolution with
// 3 x 3 coverage sampling, so edges get a few hand-placed-looking
// anti-alias tones instead of jaggies. Every pixel keeps its material, light
// level and surface normal, so later passes can shade, cast shadows, add a
// second coloured light, outline contact edges and rim-light the silhouette.
// Tones come from short colour ramps (optionally with ordered Bayer dithering
// between steps), which keeps the result reading as pixel art. `view` lets a
// picture be drawn in design units and zoomed. No randomness except a seeded
// hash and PRNG, so the same portrait is always the same picture.

export type RGB = [number, number, number];

export function hex(h: string): RGB {
  const n = parseInt(h.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function mix(a: RGB, b: RGB, t: number): RGB {
  return [Math.round(a[0] + (b[0] - a[0]) * t), Math.round(a[1] + (b[1] - a[1]) * t), Math.round(a[2] + (b[2] - a[2]) * t)];
}

/** A material: darkest to lightest, plus the outline tone. */
export interface Ramp {
  tones: RGB[];
  outline: RGB;
}

export function ramp(...tones: string[]): Ramp {
  const t = tones.map(hex);
  const d = t[0]!;
  return { tones: t, outline: [Math.round(d[0] * 0.55), Math.round(d[1] * 0.55), Math.round(d[2] * 0.62)] };
}

const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map((v) => (v + 0.5) / 16);
export function bayer(x: number, y: number): number {
  return BAYER[(y & 3) * 4 + (x & 3)]!;
}

/** A seeded hash in 0..1 for texture noise. */
export function hash(x: number, y: number, seed: number): number {
  let h = (x * 374761393 + y * 668265263 + seed * 2246822519) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** A small seeded PRNG (mulberry32). */
export function prng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Key light: upper left, towards the viewer.
const LN = Math.hypot(-0.52, -0.62, 0.58);
const LX = -0.52 / LN;
const LY = -0.62 / LN;
const LZ = 0.58 / LN;
const HN = Math.hypot(LX, LY, LZ + 1);
const HX = LX / HN;
const HY = LY / HN;
const HZ = (LZ + 1) / HN;

export type Pt = [number, number];

/** Anything that can be rasterised: a bounding box, an inside test and a surface normal. */
export interface Shape {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  inside(x: number, y: number): boolean;
  /** Writes nx, ny, nz and an "along" parameter (0..1) into out. */
  normal(x: number, y: number, out: Float64Array): void;
}

function norm3(o: Float64Array): void {
  const l = Math.sqrt(o[0]! * o[0]! + o[1]! * o[1]! + o[2]! * o[2]!) || 1;
  o[0]! /= l;
  o[1]! /= l;
  o[2]! /= l;
}

/** An ellipse shaded as the front of an ellipsoid; `rot` in radians. */
export function ellipse(cx: number, cy: number, rx: number, ry: number, rot = 0): Shape {
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  const R = Math.max(rx, ry) + 1;
  return {
    x0: cx - R,
    y0: cy - R,
    x1: cx + R,
    y1: cy + R,
    inside(x, y) {
      const dx = x - cx;
      const dy = y - cy;
      const u = (dx * c + dy * s) / rx;
      const v = (-dx * s + dy * c) / ry;
      return u * u + v * v <= 1;
    },
    normal(x, y, o) {
      const dx = x - cx;
      const dy = y - cy;
      const u = (dx * c + dy * s) / rx;
      const v = (-dx * s + dy * c) / ry;
      const d = Math.min(1, u * u + v * v);
      o[0] = u * c - v * s;
      o[1] = u * s + v * c;
      o[2] = Math.sqrt(1 - d);
      o[3] = (v + 1) / 2;
      norm3(o);
    },
  };
}

/** Closed Catmull-Rom smoothing of a point loop. */
export function smooth(points: Pt[], steps = 6, closed = true): Pt[] {
  const out: Pt[] = [];
  const n = points.length;
  const segs = closed ? n : n - 1;
  for (let i = 0; i < segs; i++) {
    const p0 = points[closed ? (i - 1 + n) % n : Math.max(0, i - 1)]!;
    const p1 = points[i]!;
    const p2 = points[(i + 1) % n]!;
    const p3 = points[closed ? (i + 2) % n : Math.min(n - 1, i + 2)]!;
    for (let k = 0; k < steps; k++) {
      const t = k / steps;
      const t2 = t * t;
      const t3 = t2 * t;
      out.push([
        0.5 * (2 * p1[0] + (-p0[0] + p2[0]) * t + (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 + (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3),
        0.5 * (2 * p1[1] + (-p0[1] + p2[1]) * t + (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 + (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3),
      ]);
    }
  }
  if (!closed) out.push(points[n - 1]!);
  return out;
}

/**
 * A filled polygon whose surface rounds over within `bevel` pixels of its
 * edge (a small bevel reads as a metal plate, a large one as cloth or flesh).
 */
export function poly(points: Pt[], bevel = 3, profile = 1): Shape {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const [x, y] of points) {
    x0 = Math.min(x0, x);
    y0 = Math.min(y0, y);
    x1 = Math.max(x1, x);
    y1 = Math.max(y1, y);
  }
  const n = points.length;
  // Edges j -> i as flat arrays, each with a box grown by the bevel.
  const AX = new Float64Array(n);
  const AY = new Float64Array(n);
  const EX = new Float64Array(n);
  const EY = new Float64Array(n);
  const IL2 = new Float64Array(n);
  const BX0 = new Float64Array(n);
  const BY0 = new Float64Array(n);
  const BX1 = new Float64Array(n);
  const BY1 = new Float64Array(n);
  const bv = Math.max(0.01, bevel);
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [ax, ay] = points[j]!;
    const [qx, qy] = points[i]!;
    AX[i] = ax;
    AY[i] = ay;
    EX[i] = qx - ax;
    EY[i] = qy - ay;
    IL2[i] = 1 / (EX[i]! * EX[i]! + EY[i]! * EY[i]! || 1);
    BX0[i] = Math.min(ax, qx) - bv;
    BY0[i] = Math.min(ay, qy) - bv;
    BX1[i] = Math.max(ax, qx) + bv;
    BY1[i] = Math.max(ay, qy) + bv;
  }
  // Scanline crossings, cached for the last few rows asked about.
  const rowY: number[] = [];
  const rowX: Float64Array[] = [];
  const crossings = (y: number): Float64Array => {
    for (let k = 0; k < rowY.length; k++) if (rowY[k] === y) return rowX[k]!;
    const xs: number[] = [];
    for (let i = 0; i < n; i++) {
      const ay = AY[i]!;
      const by = ay + EY[i]!;
      if (ay > y !== by > y) xs.push(AX[i]! + (EX[i]! * (y - ay)) / EY[i]!);
    }
    const out = Float64Array.from(xs);
    if (rowY.length >= 6) {
      rowY.shift();
      rowX.shift();
    }
    rowY.push(y);
    rowX.push(out);
    return out;
  };
  return {
    x0,
    y0,
    x1,
    y1,
    inside(x, y) {
      if (x < x0 || x > x1 || y < y0 || y > y1) return false;
      const xs = crossings(y);
      let c = false;
      for (let k = 0; k < xs.length; k++) if (x < xs[k]!) c = !c;
      return c;
    },
    normal(x, y, o) {
      let best = Infinity;
      let bx = 0;
      let by = 0;
      for (let i = 0; i < n; i++) {
        if (x < BX0[i]! || x > BX1[i]! || y < BY0[i]! || y > BY1[i]!) continue;
        const ax = AX[i]!;
        const ay = AY[i]!;
        const ex = EX[i]!;
        const ey = EY[i]!;
        let t = ((x - ax) * ex + (y - ay) * ey) * IL2[i]!;
        if (t < 0) t = 0;
        else if (t > 1) t = 1;
        const cx = ax + ex * t;
        const cy = ay + ey * t;
        const d = (x - cx) * (x - cx) + (y - cy) * (y - cy);
        if (d < best) {
          best = d;
          bx = cx;
          by = cy;
        }
      }
      const d = Math.sqrt(best);
      const k = Math.min(1, d / bv);
      const s = Math.pow(1 - k, profile);
      const ox = d > 0 && d < Infinity ? (bx - x) / d : 0;
      const oy = d > 0 && d < Infinity ? (by - y) / d : 0;
      o[0] = ox * s;
      o[1] = oy * s;
      o[2] = Math.sqrt(Math.max(0, 1 - s * s));
      o[3] = (y - y0) / Math.max(1, y1 - y0);
      norm3(o);
    },
  };
}

/** A smooth closed shape through the given points. */
export function blob(points: Pt[], bevel = 4, profile = 1): Shape {
  return poly(smooth(points, 6, true), bevel, profile);
}

export interface StrokeOpts {
  /** Taper both ends to a point (0 none, 1 full). */
  taper?: number;
  /** Smooth the control points with Catmull-Rom. */
  smooth?: boolean;
  /** Flatten the cylinder so shading is softer (0..1). */
  flat?: number;
}

/** A tapered stroke along a path: hair locks, brows, straps, cables. */
export function stroke(points: Pt[], w0: number, w1 = w0, opts: StrokeOpts = {}): Shape {
  const pts = opts.smooth === false ? points : smooth(points, 8, false);
  const n = pts.length;
  const cum = [0];
  for (let i = 1; i < n; i++) cum.push(cum[i - 1]! + Math.hypot(pts[i]![0] - pts[i - 1]![0], pts[i]![1] - pts[i - 1]![1]));
  const L = cum[n - 1]! || 1;
  const taper = opts.taper ?? 0;
  const flat = opts.flat ?? 0;
  const half = (t: number): number => {
    let w = (w0 + (w1 - w0) * t) / 2;
    if (taper > 0) w *= 1 - taper * Math.pow(Math.abs(2 * t - 1), 3);
    return w;
  };
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  const W = Math.max(w0, w1) / 2 + 1;
  for (const [x, y] of pts) {
    x0 = Math.min(x0, x - W);
    y0 = Math.min(y0, y - W);
    x1 = Math.max(x1, x + W);
    y1 = Math.max(y1, y + W);
  }
  // Segments precomputed, each with a box grown by the widest half-width, so
  // most segments are skipped with four comparisons.
  const S = n - 1;
  const AX = new Float64Array(S);
  const AY = new Float64Array(S);
  const EX = new Float64Array(S);
  const EY = new Float64Array(S);
  const IL2 = new Float64Array(S);
  const IL = new Float64Array(S);
  const C0 = new Float64Array(S);
  const CL = new Float64Array(S);
  const BX0 = new Float64Array(S);
  const BY0 = new Float64Array(S);
  const BX1 = new Float64Array(S);
  const BY1 = new Float64Array(S);
  for (let i = 0; i < S; i++) {
    const [ax, ay] = pts[i]!;
    const [bx, by] = pts[i + 1]!;
    AX[i] = ax;
    AY[i] = ay;
    EX[i] = bx - ax;
    EY[i] = by - ay;
    const l2 = EX[i]! * EX[i]! + EY[i]! * EY[i]! || 1;
    IL2[i] = 1 / l2;
    IL[i] = 1 / Math.sqrt(l2);
    C0[i] = cum[i]! / L;
    CL[i] = (cum[i + 1]! - cum[i]!) / L;
    const hw = Math.max(w0, w1) / 2 + 0.05;
    BX0[i] = Math.min(ax, bx) - hw;
    BY0[i] = Math.min(ay, by) - hw;
    BX1[i] = Math.max(ax, bx) + hw;
    BY1[i] = Math.max(ay, by) + hw;
  }
  // Closest point query: inside test, and (with o) the cylinder normal and t along.
  const q = (x: number, y: number, o: Float64Array | null): boolean => {
    let best = Infinity;
    for (let i = 0; i < S; i++) {
      if (x < BX0[i]! || x > BX1[i]! || y < BY0[i]! || y > BY1[i]!) continue;
      const ex = EX[i]!;
      const ey = EY[i]!;
      const rx = x - AX[i]!;
      const ry = y - AY[i]!;
      let u = (rx * ex + ry * ey) * IL2[i]!;
      if (u < 0) u = 0;
      else if (u > 1) u = 1;
      const dx = rx - ex * u;
      const dy = ry - ey * u;
      const t = C0[i]! + CL[i]! * u;
      const r = Math.sqrt(dx * dx + dy * dy) / Math.max(0.05, half(t));
      if (r < best) {
        best = r;
        if (o) {
          const il = IL[i]!;
          const bs = (dx * -ey + dy * ex) * il >= 0 ? 1 : -1;
          const a = Math.min(1, r) * bs * (1 - flat);
          const px = -ey * il;
          const py = ex * il;
          o[0] = px * a;
          o[1] = py * a;
          o[2] = Math.sqrt(Math.max(0, 1 - a * a));
          o[3] = t;
        }
      }
    }
    return best <= 1;
  };
  return {
    x0,
    y0,
    x1,
    y1,
    inside: (x, y) => q(x, y, null),
    normal(x, y, o) {
      q(x, y, o);
      norm3(o);
    },
  };
}

/** Shape a's outline with shape b's (smoother) surface normals. */
export function withNormal(a: Shape, b: Shape): Shape {
  return { x0: a.x0, y0: a.y0, x1: a.x1, y1: a.y1, inside: (x, y) => a.inside(x, y), normal: (x, y, o) => b.normal(x, y, o) };
}

/** An ellipsoid normal field that keeps going (clamped) outside its ellipse. */
export function dome(cx: number, cy: number, rx: number, ry: number): Shape {
  return {
    x0: cx - rx,
    y0: cy - ry,
    x1: cx + rx,
    y1: cy + ry,
    inside: () => true,
    normal(x, y, o) {
      const u = (x - cx) / rx;
      const v = (y - cy) / ry;
      const d = u * u + v * v;
      const k = d > 0.96 ? Math.sqrt(0.96 / d) : 1;
      o[0] = u * k;
      o[1] = v * k;
      o[2] = Math.sqrt(Math.max(0, 1 - d * k * k));
      o[3] = (v + 1) / 2;
      norm3(o);
    },
  };
}

/** Shape a minus shape b (normals from a). */
export function sub(a: Shape, b: Shape): Shape {
  return { x0: a.x0, y0: a.y0, x1: a.x1, y1: a.y1, inside: (x, y) => a.inside(x, y) && !b.inside(x, y), normal: (x, y, o) => a.normal(x, y, o) };
}

/** Shape a clipped to shape b (normals from a). */
export function and(a: Shape, b: Shape): Shape {
  return { x0: Math.max(a.x0, b.x0), y0: Math.max(a.y0, b.y0), x1: Math.min(a.x1, b.x1), y1: Math.min(a.y1, b.y1), inside: (x, y) => a.inside(x, y) && b.inside(x, y), normal: (x, y, o) => a.normal(x, y, o) };
}

/** Soft elliptical falloff, 1 at the centre to 0 at the edge. */
export function falloff(cx: number, cy: number, rx: number, ry: number, p = 1): (x: number, y: number) => number {
  return (x, y) => {
    const u = (x - cx) / rx;
    const v = (y - cy) / ry;
    const d = Math.sqrt(u * u + v * v);
    return d >= 1 ? 0 : Math.pow(1 - d, p);
  };
}

export interface Paint {
  /** Added to the light level. */
  bias?: number;
  amb?: number;
  diff?: number;
  /** Vertical gradient across the shape's box (top lighter). */
  grad?: number;
  /** Level change along a stroke or down an ellipse (o[3] - 0.5). */
  along?: number;
  spec?: number;
  shine?: number;
  /** Mirror-like metal: reflects a bright sky above a dark horizon. */
  chrome?: number;
  /** Seeded texture noise amplitude. */
  noise?: number;
  seed?: number;
  /** Only paint over pixels of these materials. */
  clip?: Ramp | Ramp[];
  /** Coverage anti-aliasing (default on). */
  aa?: boolean;
  /** Paint colour only, keeping the material underneath (eyes, mouths, marks). */
  decal?: boolean;
  /** Width of the dither band between tones (0..0.5, default 0.2). */
  dither?: number;
}

const tmp = new Float64Array(4);
const N4 = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
] as const;

export class Pixels {
  readonly w: number;
  readonly h: number;
  /** Colour per pixel (or null for transparent). */
  readonly rgb: (RGB | null)[];
  /** Material index per pixel (-1 none). */
  readonly mat: Int16Array;
  readonly lvl: Float32Array;
  readonly nrm: Float32Array;
  readonly ord: Int32Array;
  readonly cov: Float32Array;
  readonly lock: Uint8Array;
  readonly under: (RGB | null)[];
  readonly ramps: Ramp[] = [];
  private dith: Float32Array;
  private seq = 1;
  // Design-to-pixel view: pixel = design * k + offset.
  private k = 1;
  private ox = 0;
  private oy = 0;

  constructor(w: number, h: number) {
    this.w = w;
    this.h = h;
    const n = w * h;
    this.rgb = new Array<RGB | null>(n).fill(null);
    this.under = new Array<RGB | null>(n).fill(null);
    this.mat = new Int16Array(n).fill(-1);
    this.lvl = new Float32Array(n);
    this.nrm = new Float32Array(n * 3);
    this.ord = new Int32Array(n);
    this.cov = new Float32Array(n).fill(1);
    this.lock = new Uint8Array(n);
    this.dith = new Float32Array(n).fill(0.2);
  }

  /**
   * Draw at a zoom: everything after this call is given in design
   * coordinates, and design point (fx, fy) lands on pixel (px, py).
   */
  view(k: number, fx: number, fy: number, px = this.w / 2, py = this.h / 2): void {
    this.k = k;
    this.ox = px - fx * k;
    this.oy = py - fy * k;
  }

  /** Pixel to design coordinates. */
  dx(x: number): number {
    return (x - this.ox) / this.k;
  }
  dy(y: number): number {
    return (y - this.oy) / this.k;
  }

  matId(r: Ramp): number {
    let i = this.ramps.indexOf(r);
    if (i < 0) {
      this.ramps.push(r);
      i = this.ramps.length - 1;
    }
    return i;
  }

  private clipSet(clip: Ramp | Ramp[] | undefined): Set<number> | null {
    if (!clip) return null;
    const list = Array.isArray(clip) ? clip : [clip];
    return new Set(list.map((r) => this.matId(r)));
  }

  /** Pick a tone from a light level 0..1, dithered between neighbouring tones. */
  tone(r: Ramp, level: number, x: number, y: number, band = 0.2): RGB {
    const n = r.tones.length;
    const v = Math.max(0, Math.min(0.999, level)) * (n - 1);
    const lo = Math.floor(v);
    const frac = v - lo;
    const a = 0.5 - band;
    const b = 0.5 + band;
    if (frac < a) return r.tones[lo]!;
    if (frac > b) return r.tones[Math.min(n - 1, lo + 1)]!;
    return r.tones[Math.min(n - 1, (frac - a) / (b - a) > bayer(x, y) ? lo + 1 : lo)]!;
  }

  set(x: number, y: number, c: RGB, m = -1): void {
    x = Math.floor(x * this.k + this.ox);
    y = Math.floor(y * this.k + this.oy);
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return;
    const i = y * this.w + x;
    this.rgb[i] = c;
    if (m >= 0) this.mat[i] = m;
    this.lock[i] = 1;
  }

  /** Blend a colour into one pixel (for single-pixel accents). */
  dab(x: number, y: number, c: RGB, a = 1): void {
    x = Math.floor(x * this.k + this.ox);
    y = Math.floor(y * this.k + this.oy);
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return;
    const i = y * this.w + x;
    const o = this.rgb[i];
    this.rgb[i] = o ? mix(o, c, a) : c;
    this.lock[i] = 1;
  }

  get(x: number, y: number): RGB | null {
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return null;
    return this.rgb[y * this.w + x] ?? null;
  }

  rect(x0: number, y0: number, w: number, h: number, c: RGB): void {
    for (let y = y0; y < y0 + h; y++) for (let x = x0; x < x0 + w; x++) this.set(x, y, c);
  }

  /** Vertical gradient background, banded and dithered. */
  background(top: RGB, bottom: RGB, bands = 7): void {
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        const k = y / (this.h - 1);
        const band = Math.min(1, Math.floor(k * bands + bayer(x, y)) / bands);
        const i = y * this.w + x;
        this.rgb[i] = mix(top, bottom, band);
        this.mat[i] = -1;
      }
    }
  }

  private level(o: Paint, sh: Shape, _x: number, y: number, n: Float64Array, px: number, py: number): number {
    const nx = n[0]!;
    const ny = n[1]!;
    const nz = n[2]!;
    const d = nx * LX + ny * LY + nz * LZ;
    let l = (o.amb ?? 0.18) + (o.diff ?? 0.82) * Math.max(0, d) + (o.bias ?? 0);
    if (o.spec) l += o.spec * Math.pow(Math.max(0, nx * HX + ny * HY + nz * HZ), o.shine ?? 24);
    if (o.chrome) {
      // Reflect the view ray: up is sky, a dark horizon band, then warm ground.
      const ry = 2 * nz * ny;
      const up = -ry;
      const env = up > 0.12 ? 0.78 + 0.22 * Math.min(1, up) : up > -0.18 ? 0.12 : 0.42 - 0.2 * Math.min(1, -up - 0.18);
      l = l * (1 - o.chrome) + env * o.chrome + (o.bias ?? 0) * o.chrome;
    }
    if (o.grad) l -= o.grad * ((y - sh.y0) / Math.max(1, sh.y1 - sh.y0) - 0.5);
    if (o.along) l += o.along * (n[3]! - 0.5);
    if (o.noise) l += o.noise * (hash(px, py, o.seed ?? 7) - 0.5);
    return l;
  }

  /** Rasterise a shape in a material. */
  paint(sh: Shape, r: Ramp, o: Paint = {}): void {
    const m = this.matId(r);
    const clip = this.clipSet(o.clip);
    const aa = o.aa !== false;
    const band = o.dither ?? 0;
    const order = this.seq++;
    const k = this.k;
    const xa = Math.max(0, Math.floor(sh.x0 * k + this.ox));
    const ya = Math.max(0, Math.floor(sh.y0 * k + this.oy));
    const xb = Math.min(this.w - 1, Math.ceil(sh.x1 * k + this.ox));
    const yb = Math.min(this.h - 1, Math.ceil(sh.y1 * k + this.oy));
    for (let y = ya; y <= yb; y++) {
      for (let x = xa; x <= xb; x++) {
        const i = y * this.w + x;
        if (clip && !clip.has(this.mat[i]!)) continue;
        let cov: number;
        if (aa) {
          // Centre and corners first; only edge pixels need the other four samples.
          const a = this.dx(x + 1 / 6);
          const b = this.dx(x + 0.5);
          const e = this.dx(x + 5 / 6);
          const f = this.dy(y + 1 / 6);
          const g = this.dy(y + 0.5);
          const hh = this.dy(y + 5 / 6);
          let n = (sh.inside(b, g) ? 1 : 0) + (sh.inside(a, f) ? 1 : 0) + (sh.inside(e, f) ? 1 : 0) + (sh.inside(a, hh) ? 1 : 0) + (sh.inside(e, hh) ? 1 : 0);
          if (n === 0) continue;
          if (n < 5) n += (sh.inside(b, f) ? 1 : 0) + (sh.inside(a, g) ? 1 : 0) + (sh.inside(e, g) ? 1 : 0) + (sh.inside(b, hh) ? 1 : 0);
          else n = 9;
          const c = n / 9;
          if (c < 0.3) continue;
          cov = c >= 0.75 ? 1 : c >= 0.5 ? 0.66 : 0.4;
        } else {
          if (!sh.inside(this.dx(x + 0.5), this.dy(y + 0.5))) continue;
          cov = 1;
        }
        const ux = this.dx(x + 0.5);
        const uy = this.dy(y + 0.5);
        sh.normal(ux, uy, tmp);
        const l = this.level(o, sh, ux, uy, tmp, x, y);
        const col = this.tone(r, l, x, y, band);
        const base = this.rgb[i];
        if (cov === 1 || !base) {
          this.rgb[i] = col;
          this.under[i] = null;
          this.cov[i] = 1;
        } else {
          this.rgb[i] = mix(base, col, cov);
          this.under[i] = base;
          this.cov[i] = cov;
        }
        if (o.decal) {
          this.lock[i] = 1;
          continue;
        }
        if (cov >= 0.5) {
          this.mat[i] = m;
          this.lvl[i] = l;
          this.nrm[i * 3] = tmp[0]!;
          this.nrm[i * 3 + 1] = tmp[1]!;
          this.nrm[i * 3 + 2] = tmp[2]!;
          this.ord[i] = order;
          this.lock[i] = 0;
          this.dith[i] = band;
        }
      }
    }
  }

  private recolour(i: number): void {
    const m = this.mat[i]!;
    if (m < 0) return;
    const x = i % this.w;
    const y = (i - x) / this.w;
    const col = this.tone(this.ramps[m]!, this.lvl[i]!, x, y, this.dith[i]!);
    const u = this.under[i];
    this.rgb[i] = u && this.cov[i]! < 1 ? mix(u, col, this.cov[i]!) : col;
  }

  /** Shift the light level by fn(x, y) (shadows < 0, highlights > 0). */
  shade(fn: (x: number, y: number) => number, clip?: Ramp | Ramp[], box?: [number, number, number, number]): void {
    const cs = this.clipSet(clip);
    const k = this.k;
    const [xa, ya, xb, yb] = box ? [box[0] * k + this.ox, box[1] * k + this.oy, box[2] * k + this.ox, box[3] * k + this.oy] : [0, 0, this.w - 1, this.h - 1];
    for (let y = Math.max(0, Math.floor(ya)); y <= Math.min(this.h - 1, Math.ceil(yb)); y++) {
      for (let x = Math.max(0, Math.floor(xa)); x <= Math.min(this.w - 1, Math.ceil(xb)); x++) {
        const i = y * this.w + x;
        if (this.mat[i]! < 0) continue;
        if (cs && !cs.has(this.mat[i]!)) continue;
        const d = fn(this.dx(x + 0.5), this.dy(y + 0.5));
        if (d === 0) continue;
        if (this.lock[i]) {
          const c = this.rgb[i]!;
          this.rgb[i] = d < 0 ? mix(c, [0, 0, 0], Math.min(0.8, -d * 0.7)) : mix(c, [255, 255, 255], Math.min(0.8, d * 0.5));
          continue;
        }
        this.lvl[i] = this.lvl[i]! + d;
        this.recolour(i);
      }
    }
  }

  /** Shade inside a shape by a constant amount (soft-edged by coverage). */
  shadeShape(sh: Shape, d: number, clip?: Ramp | Ramp[]): void {
    this.shade(
      (x, y) => {
        let k = 0;
        for (let sy = -1; sy <= 1; sy++) for (let sx = -1; sx <= 1; sx++) if (sh.inside(x + sx / (3 * this.k), y + sy / (3 * this.k))) k++;
        return (d * k) / 9;
      },
      clip,
      [sh.x0, sh.y0, sh.x1, sh.y1],
    );
  }

  /** Blend a colour over pixels by fn(x, y) alpha, quantised and dithered. */
  tint(fn: (x: number, y: number) => number, c: RGB, clip?: Ramp | Ramp[], steps = 4, dither = false): void {
    const cs = this.clipSet(clip);
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        const i = y * this.w + x;
        if (cs && !cs.has(this.mat[i]!)) continue;
        const a = fn(this.dx(x + 0.5), this.dy(y + 0.5));
        if (a <= 0) continue;
        const q = Math.min(1, dither ? Math.floor(a * steps + bayer(x, y)) / steps : Math.round(a * steps) / steps);
        if (q <= 0) continue;
        const o = this.rgb[i];
        if (o) this.rgb[i] = mix(o, c, q);
      }
    }
  }

  /** Additive coloured light by fn(x, y), banded and dithered. `bgOnly` limits it to the background. */
  glow(fn: (x: number, y: number) => number, c: RGB, steps = 5, where: 'all' | 'bg' | 'fg' = 'all'): void {
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        const i = y * this.w + x;
        if (where === 'bg' && this.mat[i]! >= 0) continue;
        if (where === 'fg' && this.mat[i]! < 0) continue;
        const a = fn(this.dx(x + 0.5), this.dy(y + 0.5));
        if (a <= 0) continue;
        const q = Math.floor(a * steps + bayer(x, y)) / steps;
        if (q <= 0) continue;
        const o = this.rgb[i] ?? [0, 0, 0];
        this.rgb[i] = [Math.min(255, Math.round(o[0] + c[0] * q)), Math.min(255, Math.round(o[1] + c[1] * q)), Math.min(255, Math.round(o[2] + c[2] * q))];
      }
    }
  }

  /**
   * A second, coloured light from direction (dx, dy, dz) on painted surfaces,
   * in clean bands (no dither) so it reads as hand-placed bounce light.
   */
  light(dx: number, dy: number, dz: number, c: RGB, strength: number, clip?: Ramp | Ramp[], steps = 3, fn?: (x: number, y: number) => number, from = 0.15): void {
    const cs = this.clipSet(clip);
    const l = Math.hypot(dx, dy, dz);
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        const i = y * this.w + x;
        const m = this.mat[i]!;
        if (m < 0 || (cs && !cs.has(m))) continue;
        let d = (this.nrm[i * 3]! * dx + this.nrm[i * 3 + 1]! * dy + this.nrm[i * 3 + 2]! * dz) / l;
        if (fn) d *= fn(this.dx(x + 0.5), this.dy(y + 0.5));
        d = (d - from) / (1 - from);
        if (d <= 0) continue;
        const q = Math.round(Math.min(1, d) * steps) / steps * strength;
        if (q <= 0) continue;
        const o = this.rgb[i]!;
        // Screen blend towards the light colour.
        const sc: RGB = [255 - ((255 - o[0]) * (255 - c[0])) / 255, 255 - ((255 - o[1]) * (255 - c[1])) / 255, 255 - ((255 - o[2]) * (255 - c[2])) / 255];
        this.rgb[i] = mix(o, sc, q);
      }
    }
  }

  /**
   * Outlines: the silhouette against the background in each material's
   * outline tone, and (optionally) a darker contact line where a shape sits
   * behind a later one.
   */
  outline(inner = 0.22, skip?: Ramp[]): void {
    const W = this.w;
    const sk = new Set((skip ?? []).map((r) => this.matId(r)));
    const sil: number[] = [];
    const con: number[] = [];
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < W; x++) {
        const i = y * W + x;
        const m = this.mat[i]!;
        if (m < 0) continue;
        let edge = false;
        let behind = false;
        for (const [dx, dy] of N4) {
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= W || ny >= this.h) continue;
          const j = ny * W + nx;
          const nm = this.mat[j]!;
          if (nm < 0) edge = true;
          else if (nm !== m && this.ord[j]! > this.ord[i]!) behind = true;
        }
        if (edge && !sk.has(m)) sil.push(i);
        else if (behind && inner > 0 && !sk.has(m)) con.push(i);
      }
    }
    for (const i of sil) this.rgb[i] = this.ramps[this.mat[i]!]!.outline;
    for (const i of con) {
      if (this.lock[i]) continue;
      this.lvl[i] = this.lvl[i]! - inner;
      this.recolour(i);
    }
  }

  /** Rim light on the silhouette: pixels with background `side` pixels away. */
  rim(c: RGB, side: 1 | -1 = 1, strength = 0.6, width = 1, fn?: (x: number, y: number) => number): void {
    const hits: [number, number][] = [];
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        const i = y * this.w + x;
        if (this.mat[i]! < 0) continue;
        for (let k = 1; k <= width + 1; k++) {
          const nx = x + side * k;
          if (nx < 0 || nx >= this.w) break;
          if (this.mat[y * this.w + nx]! < 0) {
            if (k > 1) {
              const a = strength * (fn ? fn(this.dx(x + 0.5), this.dy(y + 0.5)) : 1);
              if (a > 0) hits.push([i, a]);
            }
            break;
          }
        }
      }
    }
    for (const [i, a] of hits) this.rgb[i] = mix(this.rgb[i]!, c, a);
  }

  /** Paint to a canvas at native size. */
  toCanvas(): HTMLCanvasElement {
    const cv = document.createElement('canvas');
    cv.width = this.w;
    cv.height = this.h;
    const ctx = cv.getContext('2d')!;
    const img = ctx.createImageData(this.w, this.h);
    for (let i = 0; i < this.w * this.h; i++) {
      const c = this.rgb[i];
      if (!c) continue;
      img.data[i * 4] = c[0];
      img.data[i * 4 + 1] = c[1];
      img.data[i * 4 + 2] = c[2];
      img.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    return cv;
  }
}
